import React, {Fragment} from 'react'
import {connect} from 'react-redux'
import styles from './GuidePageEditor.scss'
import {infoMessage} from 'lib/styleguide/base.scss'
import {Map} from 'immutable'
import {ImageBlock} from './ImageBlock'
import {RouteBlock} from './RouteBlock'
import {ElevationBlock} from './ElevationBlock'
import {RichTextEditor} from 'common/RichTextEditor'
import * as blockUtils from 'lib/blockUtils'
import {DefaultDraftBlockRenderMap, RichUtils, EditorState, SelectionState} from 'draft-js'
import {Icon} from 'common/Icon'
import cx from 'classnames'
import p from 'prop-types'
import {updateGuidePage} from 'lib/api/experiences/guides'

const blockRenderMap = DefaultDraftBlockRenderMap.filter((value, key) => [
  'header-two', 'unordered-list-item', 'unstyled', 'ordered-list-item'
].includes(key))

class _GuidePageEditor extends React.PureComponent {
  static propTypes = {
    guide: p.object.isRequired,
    onSave: p.func.isRequired
  }

  componentDidMount = () => {
    this.focusEditor()
  }

  focusEditor = () => {
    this._editor && this._editor.focus()
  }

  setEditorRef = editor => {
    this._editor = editor
  }

  getEditorState = () => {
    return this.props.editorState
  }

  resetEditorMode = () => {
    this._editor.exitCurrentMode()
  }

  onChange = (editorState, cb) => {
    this.props.onChange(editorState)
  }

  onAtomicBlockChange = (block, update) => {
    const {editorState} = this.props
    const contentState = editorState.getCurrentContent()
    const newContentState = contentState
      // applying Map() to update prevents immutable from deeply converting values
      .setIn(['blockMap', block.getKey(), 'data'], block.getData().merge(Map(update)))
      .setIn(['selectionAfter', 'hasFocus'], editorState.getSelection().getHasFocus())
    this.onChange(EditorState.push(editorState, newContentState, 'change-block-data'))
  }

  blockRenderer = (block) => {
    const mappings = {
      image: ImageBlock,
      route: RouteBlock,
      elevation: ElevationBlock,
      separator: () => <hr />
    }

    if (block.getType() === 'atomic') {
      const {guide, editorState} = this.props
      const contentState = editorState.getCurrentContent()
      const selection = editorState.getSelection()
      return {
        component: mappings[block.getData().get('type')],
        editable: false,
        props: {
          guide: guide,
          getEditorState: this.getEditorState,
          setEditorState: this.onChange,
          resetEditorMode: this.resetEditorMode,
          onChange: (update) => this.onAtomicBlockChange(block, update),
          onSelected: () => this.selectBlock(block.getKey()),
          isSelected: blockUtils.selectedBlocks(contentState, selection).has(block.getKey())
        }
      }
    }
    return null
  }

  selectBlock = (blockKey) => {
    const {editorState} = this.props
    const contentState = editorState.getCurrentContent()
    // TODO future optimization: if block is already selected, return.
    // Issue with multiple atomic blocks selected at once needs to be resolved
    // before this can be implemented though, see comments in GalleryBlock grabFocusIfSelected
    const selection = SelectionState.createEmpty(blockKey).merge({
      hasFocus: true,
      focusOffset: contentState.getBlockForKey(blockKey).getLength()
    })
    const newContentState = contentState.set('selectionAfter', selection)
    const newEditorState = EditorState.push(
      editorState,
      newContentState,
      'change-block-data' // not strictly accurate, but eh
    )
    console.log('setting selection to ', selection.serialize())
    this.onChange(newEditorState)
  }

  toggleLive = () => {
    const {guide, updateGuidePage} = this.props
    const {isLive} = guide

    updateGuidePage(guide.set('isLive', !isLive))
  }

  render () {
    const {guide, savePending, hasRoutes} = this.props
    const {editorState} = this.props

    return (
      <Fragment>
        <div className={styles.GuidePageEditor}>
          <Toolbar
            editorState={editorState}
            onChange={this.onChange}
            restoreFocus={this.focusEditor}
            onSave={this.onSave}
            savePending={savePending}
            hasRoutes={hasRoutes} />
          <RichTextEditor
            ref={this.setEditorRef}
            className={styles.Editor}
            blockRendererFn={this.blockRenderer}
            blockRenderMap={blockRenderMap}
            editorState={editorState}
            onChange={this.onChange}
            placeholder='Add details to your guide here...' />
        </div>
        <LiveMessage isLive={guide.isLive} onClick={() => this.toggleLive()} />
      </Fragment>
    )
  }
}

const LiveMessage = ({isLive, onClick}) => {
  const message = isLive
    ? '*Changes to this Live Section will be updated automatically and will not require you to publish updates.'
    : '*Changes to this section require you to publish updates.'
  const link = isLive
    ? 'Change to Offline Section'
    : 'Change to Live Section'

  return <div className={styles.liveInfo}>
    <div className={infoMessage} style={{borderRadius: '2px', marginBottom: '0px'}}>
      {message} <a onClick={onClick}>{link}</a>
    </div>
  </div>
}

class Toolbar extends React.PureComponent {
  changeStyle = (newStyle) => {
    const {editorState} = this.props
    if (newStyle === 'header-two') {
      // special case for converting to headings where we nuke the inline styles
      const newEditorState = blockUtils.convertBlockToHeader(editorState)
      this.props.onChange(newEditorState, this.props.restoreFocus)
    } else {
      // otherwise just let RichUtils handle it
      const newEditorState = RichUtils.toggleBlockType(editorState, newStyle)
      this.props.onChange(newEditorState, this.props.restoreFocus)
    }
  }

  insertBlock = (type) => {
    const editorState = blockUtils.insertAtomicBlock(this.props.editorState, {type})
    this.props.onChange(editorState)
  }

  render () {
    return (
      <div className={styles.Toolbar}>
        <ToolbarGroup>
          <ToolbarButton title='Heading' onClick={() => this.changeStyle('header-two')}>
            <Icon i='baseline-title' size={16} />
          </ToolbarButton>
          <ToolbarButton title='Numbered list' onClick={() => this.changeStyle('ordered-list-item')}>
            <Icon i='baseline-format_list_numbered' size={16} />
          </ToolbarButton>
          <ToolbarButton title='Bulleted list' onClick={() => this.changeStyle('unordered-list-item')}>
            <Icon i='baseline-format_list_bulleted' size={16} />
          </ToolbarButton>
          <ToolbarButton title='Clear formatting' onClick={() => this.changeStyle('unstyled')}>
            <Icon i='baseline-format_clear' size={16} />
          </ToolbarButton>
          <ToolbarButton title='Horizontal Rule' onClick={() => this.insertBlock('separator')}>
            <Icon i='baseline-border_horizontal' size={16} />
          </ToolbarButton>
          <ToolbarButton title='Image' onClick={() => this.insertBlock('image')}>
            <Icon i='md-images' size={16} />
          </ToolbarButton>
          <ToolbarButton title='Route' onClick={() => this.insertBlock('route')}>
            <Icon i='md-pin' size={16} />
          </ToolbarButton>
          <ToolbarButton title='Elevation Profile' onClick={() => this.insertBlock('elevation')}>
            <Icon i='cue-summit' size={16} />
          </ToolbarButton>
        </ToolbarGroup>
      </div>
    )
  }
}

const ToolbarGroup = ({children}) => <div className={styles.ToolbarGroup}>{children}</div>

const ToolbarButton = ({children, onClick, disabled, title}) => <div title={title} className={cx(styles.ToolbarButton, disabled && styles.disabled)} onClick={(e) => !disabled && onClick(e)}>{children}</div>

const mapDispatchToProps = {
  updateGuidePage: updateGuidePage
}

export const GuidePageEditor = connect(
  null,
  mapDispatchToProps
)(_GuidePageEditor)
